import Link from 'next/link'
import { FormEvent, useRef, useState } from 'react'
import { Input, Card, Button } from 'components/ui'
import { api } from 'lib/api'
import { debug, logError } from 'lib/logging'

/**
 * Signup page component
 * @description Handles user registration with email/password and email verification
 * @returns {JSX.Element} The signup page with registration forms
 */
export default function Signup() {
  let formRef = useRef<HTMLFormElement>(null)
  let [email, setEmail] = useState<string>()
  let [password, setPassword] = useState<string>()
  let [error, setError] = useState<string>()
  let [isLoading, setIsLoading] = useState<boolean>(false)

  debug(`signup`, `render`, { email, password })

  /**
   * Checks if all verification code inputs are filled
   * @description Validates that all 6 verification code inputs contain single digits
   * @returns {void}
   */
  function checkSubmit() {
    let ready = [1, 2, 3, 4, 5, 6].every((i) => {
      let d = document.querySelector(`#code-${i}`)?.[`value`]
      return d?.match(/^[0-9]$/)
    })
    debug(`auth`, `checkSubmit, ready?`, ready)
    if (ready) {
      void submitCode()
    }
  }

  /**
   * Submits the email verification code for signup
   * @description Collects the 6-digit verification code from input fields and submits to API
   * @returns {Promise<void>} Promise that resolves when verification is complete
   */
  let submitCode = async () => {
    debug(`auth`, `submitting code`)
    let code = [1, 2, 3, 4, 5, 6]
      .map((i) => {
        return document.querySelector(`#code-${i}`)?.[`value`]
      })
      .join(``)
    await api.post(`/api/auth/login`, { code, email, password })
    document.location = `/account`
  }

  /**
   * Handles signup form submission
   * @description Processes user registration and initiates email verification flow
   * @param {FormEvent<HTMLFormElement>} event - The form submission event
   * @returns {Promise<boolean>} Returns false to prevent default form submission
   */
  let submitSignup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(undefined)
    setIsLoading(true)

    let formData = new FormData(formRef.current!)

    let req = {
      email: formData.get(`email`),
      password: formData.get(`password`),
    }

    debug(`signup`, `current`, req)
    debug(`signup`, `api host`, process.env.HOST)

    try {
      await api.post(`/api/auth/signup`, req)
      setEmail(req.email as string)
      setPassword(req.password as string)
    } catch (err: any) {
      logError(`login error`, err?.message)

      // Display user-friendly error messages
      let errorMessage = err?.message || 'An unexpected error occurred'
      if (errorMessage === 'user already exists') {
        errorMessage = 'An account with this email address already exists. Please try logging in instead.'
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }

    return false
  }

  return (
    <div id='signup-page' className='center-all bg-slate-900 flex-col min-h-screen'>
      {email && password ? (
        <Card mode='primary' className='max-w-2xl'>
          <div className='font-weight-bold pb-4 text-center'>
            Please verify your email by entering the code that was just emailed to you.
          </div>

          <div>
            {[1, 2, 3, 4, 5, 6].map((i) => {
              return (
                <input
                  key={i}
                  style={{ width: `100px`, height: `100px` }}
                  className='border-5 m-1 border border-cyan-400 text-center text-5xl text-slate-800 focus:border-cyan-300 focus:outline-none focus:ring-2 focus:ring-cyan-400/50'
                  id={`code-` + i}
                  type='tel'
                  autoComplete='off'
                  placeholder='-'
                  maxLength={1}
                  tabIndex={i}
                  onPaste={(event) => {
                    event.preventDefault()
                    let code = event.clipboardData.getData(`text`)
                    if (!code.match(/^[0-9]+$/)) return
                    let digits = code.split(``)
                    digits.forEach((d, j) => {
                      document.querySelector(`#code-${j + 1}`)?.setAttribute(`value`, d || ``)
                    })
                    checkSubmit()
                  }}
                  onKeyDown={(event) => {
                    // keep non-numbers from being entered
                    if (
                      event.metaKey ||
                      event.ctrlKey ||
                      event.altKey ||
                      [`Tab`, `Backspace`, `Delete`].includes(event.key)
                    )
                      return
                    if (!event.key.match(/[0-9]/)) return event.preventDefault()
                  }}
                  onKeyUp={(event) => {
                    if (!event.key.match(/[0-9]/)) return
                    // @ts-ignore
                    document.querySelector(`#code-${i + 1}`)?.focus()
                    checkSubmit()
                  }}
                />
              )
            })}
          </div>
        </Card>
      ) : (
        <>
          <Card mode='primary' className='w-[400px] mb-6'>
            <form ref={formRef} onSubmit={submitSignup}>
              <h3 className='mb-6 text-center text-cyan-100'>Create a new account</h3>
              <div className='mb-8'>
                <label htmlFor='email' className='block text-sm font-medium text-slate-100 mb-1'>
                  Email Address
                </label>
                <Input
                  name='email'
                  id='email'
                  type='email'
                  mode='secondary'
                  fullWidth={true}
                  required
                  defaultValue={process.env.DEFAULT_EMAIL}
                  onInput={() => error && setError(undefined)}
                />
              </div>
              <div className='mb-8'>
                <label htmlFor='password' className='block text-sm font-medium text-slate-100 mb-1'>
                  Password
                </label>
                <Input
                  type='password'
                  name='password'
                  id='password'
                  mode='secondary'
                  fullWidth={true}
                  aria-label='password'
                  required
                  defaultValue={process.env.DEFAULT_PASSWORD}
                  onInput={() => error && setError(undefined)}
                />
              </div>

              {error && (
                <Card mode='danger' className='mb-5' glow={true}>
                  <p className='text-center text-sm text-gray-100 mb-2'>{error}</p>
                  {error.includes('already exists') && (
                    <p className='text-center text-xs text-gray-300'>
                      <Link href='/login' className='text-danger hover:text-orange-300 underline font-medium'>
                        Click here to log in instead
                      </Link>
                    </p>
                  )}
                </Card>
              )}

              <div className='mb-5 flex justify-center'>
                <Button type='submit' disabled={isLoading} className='w-full mt-6'>
                  {isLoading ? 'Creating Account...' : 'Sign Up'}
                </Button>
              </div>
            <p className='col-span-6 my-10 text-center text-slate-300'>
              By signing up for a CheapVeep account you are agreeing to the{` `}
              <Link href='/privacy' className='font-bold text-cyan-400 underline'>
                Privacy Policy
              </Link>
              {` `}
              and{` `}
              <Link className='font-bold text-cyan-400 underline' href='/terms'>
                Terms of Service
              </Link>
              .
            </p>
            </form>
          </Card>
          <div>
              <p className='text-center text-slate-100'>
                Already have an account?{` `}
                <Link href='/login' className='text-cyan-400 hover:text-cyan-300'>
                  Log in
                </Link>
              </p>
          </div>
        </>
      )}
    </div>
  )
}
